import { createContext, useContext, useState, useEffect, PropsWithChildren } from "react";
import { useParams } from "react-router-dom";

import { AppConfigurationContext } from './appConfigurationProvider.tsx';
import { MetadataContext, MetaDataLoadingState } from './metadataProvider.tsx';
import { BeamlineDto, getBeamlineDetailsBySlug } from './api/api.ts';
import { ScanTypeCatalog } from './scanTypes.ts';


// This is a "context provider" React component for a Beamline.
// It fetches and stores all ScanType and Parameter information
// for a single Beamline and provides it to child components.

// This component should be provided with an ESAF slug or a Beamline slug,
// either as a property or as a URL parameter.
// If the component cannot find a usable value it will skip loading,
// then try again as soon as the value is changed.

enum BeamlineLoadingState { NotTriggered, Pending, Succeeded, Failed };

// The structure we are providing to components in the hierarchy below the provider
interface SampleConfigurationInterface {
  // The ESAF Slug that was provided to this provider
  beamline: BeamlineDto | undefined;
  scanTypes: ScanTypeCatalog;
  setNoun: string;
  setNounPlural: string;
  setNounCapitalized: string;

  // Track the status of loading the Beamline metadata.
  loadingState: BeamlineLoadingState;

  // A callback for when a child component makes a change to the Beamline instance.
  changed: () => void;
  // This counter is incremented whenever the Beamline data is changed.
  // Components can watch this counter to know when they need to re-render,
  // or compare existing cached data to check for a re-render.
  // Actually more straightforward than providing a registration point for callbacks,
  // since we can leverage useEffect.
  changeCounter: number;
}

const defaultInterface: SampleConfigurationInterface = {
  beamline: undefined,
  scanTypes: new ScanTypeCatalog([], []), // Should never be reached

  setNoun: "set",
  setNounPlural: "sets",
  setNounCapitalized: "Set",
  loadingState: BeamlineLoadingState.NotTriggered,
  changed: () => {},
  changeCounter: 0
}

interface ProviderProps {
  esafSlug?: string;
  beamlineSlug?: string;
}

const BeamlineContext = createContext<SampleConfigurationInterface>(defaultInterface);

const BeamlineProvider: React.FC<PropsWithChildren<ProviderProps>> = (props) => {
  let { esafSlug: esafSlugFromParams, beamlineSlug: beamlineSlugFromParams } = useParams();
  if (!esafSlugFromParams) {
    esafSlugFromParams = props.esafSlug;
  }
  if (!beamlineSlugFromParams) {
    beamlineSlugFromParams = props.beamlineSlug;
  }

  const [esafSlug, setEsafSlug] = useState<string | undefined>(esafSlugFromParams);
  const [beamlineSlug, setBeamlineSlug] = useState<string | undefined>(beamlineSlugFromParams);

  const appConfig = useContext(AppConfigurationContext);
  const metaData = useContext(MetadataContext);
  
  const [beamline, setBeamline] = useState<BeamlineDto | undefined>(undefined);
  const [scanTypes, setScanTypes] = useState<ScanTypeCatalog>(defaultInterface.scanTypes);

  const [setNoun, setSetNoun] = useState<string>(defaultInterface.setNoun);
  const [setNounPlural, setSetNounPlural] = useState<string>(defaultInterface.setNounPlural);
  const [setNounCapitalized, setSetNounCapitalized] = useState<string>(defaultInterface.setNounCapitalized);

  const [loadingState, setLoadingState] = useState<BeamlineLoadingState>(BeamlineLoadingState.NotTriggered);

  const [changeCounter, setChangeCounter] = useState<number>(0);


  useEffect(() => {
    appConfig.log('BeamlineProvider mounted');
    return () => {
      appConfig.log('BeamlineProvider unmounted');
    };
  }, []);


  useEffect(() => {
    // Proceed only if our watched is "Pending":
    if (loadingState != BeamlineLoadingState.Pending) { return; }

    const fetchData = async () => {
      try {
        if (metaData.loadingState != MetaDataLoadingState.Succeeded) {
          appConfig.log("Beamline provider: Metadata not loaded yet.");
          // Don't throw, just try again later.
          return;
        }

        let resolvedBeamline: BeamlineDto | undefined = undefined;
        if (esafSlug) {
          const esaf = metaData.esafs.bySlug.get(esafSlug.trim());
          if (!esaf) {
            setLoadingState(BeamlineLoadingState.Failed);
            appConfig.log("No ESAF found with slug: " + esafSlug);
            throw new Error("No ESAF found with slug: " + esafSlug);
          }
          resolvedBeamline = metaData.beamlines.bySlug.get(esaf.slug_beamline);
        } else if (beamlineSlug) {
         const p = beamlineSlug.trim()
          if (!p) {
            setLoadingState(BeamlineLoadingState.Failed);
            appConfig.log("Beamline slug is blank.");
            throw new Error("Beamline slug is blank");
          }
          resolvedBeamline = metaData.beamlines.bySlug.get(p);
        }

        if (!resolvedBeamline) {
          setLoadingState(BeamlineLoadingState.Failed);
          appConfig.log("No Beamline found.");
          throw new Error("No Beamline found.");
        }

        setBeamline(resolvedBeamline);
        const noun = resolvedBeamline.set_noun ? resolvedBeamline.set_noun : "set";
        setSetNoun(noun);
        setSetNounPlural(noun + "s");
        setSetNounCapitalized(noun.charAt(0).toUpperCase() + noun.slice(1));

        // Use Promise.all to ask for everything at once.
        const beamlineDetailsResult = await getBeamlineDetailsBySlug(resolvedBeamline.slug);

        if (!beamlineDetailsResult.success) {
          const message = `Error fetching metadata: ${beamlineDetailsResult.message}`;
          appConfig.log(message);
          setLoadingState(BeamlineLoadingState.Failed);
          return;
        }

        appConfig.log('Ingesting ScanTypes.');
        // Create a master container for all our sets
        const params = beamlineDetailsResult.response!.parameters;
        const scanTypes = beamlineDetailsResult.response!.scan_types;
        let scanTypeCatalog: ScanTypeCatalog = new ScanTypeCatalog(params, scanTypes);
        setScanTypes(scanTypeCatalog);
        setLoadingState(BeamlineLoadingState.Succeeded);
        return;
      } catch (error) {
        appConfig.log('Exception fetching BeamlineProvider data: ' + error);
      }
      appConfig.log('Setting BeamlineProvider loading state as failed');
      setLoadingState(BeamlineLoadingState.Failed);
    };

    appConfig.log('BeamlineProvider calling fetchData');
    fetchData();

  }, [metaData.loadingState, loadingState]);


  useEffect(() => {
    if (esafSlug === undefined && beamlineSlug === undefined) {
      appConfig.log('BeamlineProvider given undefined esafSlug and beamlineSlug');
      return;
    }
    setLoadingState(BeamlineLoadingState.Pending);
  }, [esafSlug, beamlineSlug]);


  // A callback for when a child component makes a change to the Beamline instance.
  // Note: In the future it may be prudent to pass a set ID into this,
  // to trigger separate server data update calls for each set, since each will have its own history.
  function changed() {
    appConfig.log("Called changed() in BeamlineProvider.");
    setChangeCounter(changeCounter + 1);
  };


  return (
    <BeamlineContext.Provider value={{
        beamline: beamline,
        scanTypes: scanTypes,
        setNoun: setNoun,
        setNounPlural: setNounPlural,
        setNounCapitalized: setNounCapitalized,
        loadingState: loadingState,
        changed: changed,
        changeCounter: changeCounter
    }}>
      {props.children}
    </BeamlineContext.Provider>
  )
}

export { BeamlineContext, BeamlineProvider, BeamlineLoadingState }